import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Camera,
    CheckCircle,
    Clock,
    User,
    AlertTriangle,
    LogOut,
    LogIn,
    ArrowLeft,
    Calendar,
    Search,
    VideoOff
} from 'lucide-react';

// Helper function to format time
const formatTime = (date, format = 'HH:mm:ss') => {
    const options = { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true };
    if (format === 'date') {
        return new Intl.DateTimeFormat('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }).format(date);
    }
    return new Intl.DateTimeFormat('en-US', options).format(date);
};

const AttendanceSelfie = ({ onBack }) => {
    // --- State Management ---
    const [currentTime, setCurrentTime] = useState(new Date());
    const [isPunchedIn, setIsPunchedIn] = useState(false);
    const [punchMessage, setPunchMessage] = useState('');
    const [capturedImage, setCapturedImage] = useState(null);
    const [isWebcamActive, setIsWebcamActive] = useState(false);
    const [showVideo, setShowVideo] = useState(false); // New state to control DOM rendering
    const [isLoading, setIsLoading] = useState(false);
    const [hasCamera, setHasCamera] = useState(true);

    // History & Filter State
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [history, setHistory] = useState([
        { id: 1, date: '2025-12-24', time: '09:05 AM', type: 'Check In', status: 'Verified', location: 'Office Main Gate' },
        { id: 2, date: '2025-12-24', time: '06:30 PM', type: 'Check Out', status: 'Verified', location: 'Office Main Gate' },
    ]);

    const filteredHistory = history.filter(item => item.date === selectedDate);

    // --- Refs ---
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);

    // --- Device Detection ---
    useEffect(() => {
        const checkDevices = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoInputExists = devices.some(device => device.kind === 'videoinput');
                setHasCamera(videoInputExists);
            } catch (err) {
                console.error("Error enumerating devices:", err);
                setHasCamera(false);
            }
        };
        checkDevices();
    }, []);

    // --- Webcam Control ---
    const stopWebcam = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setIsWebcamActive(false);
        setShowVideo(false);
    };

    useEffect(() => {
        const timerId = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => {
            clearInterval(timerId);
            stopWebcam();
        };
    }, []);

    const processPunchLog = (imageData = null) => {
        const newStatus = !isPunchedIn;
        const now = new Date();

        const newLog = {
            id: Date.now(),
            date: now.toISOString().split('T')[0],
            time: formatTime(now),
            type: newStatus ? 'Check In' : 'Check Out',
            status: 'Verified',
            location: imageData ? 'Selfie Verification' : 'Standard Check-in'
        };

        setHistory(prev => [newLog, ...prev]);
        if (imageData) setCapturedImage(imageData);
        setIsPunchedIn(newStatus);
        setPunchMessage(`Successfully ${newStatus ? 'Punched In' : 'Punched Out'}.`);
        
        stopWebcam();

        setTimeout(() => {
            setPunchMessage('');
            setCapturedImage(null);
        }, 4000);
    };

    // --- FIXED CAMERA FLOW FOR IOS SAFARI ---
    const handlePunch = async () => {
        if (!hasCamera) {
            processPunchLog(null);
            return;
        }

        // STEP 1: OPEN CAMERA FLOW
        if (!isWebcamActive && !capturedImage) {
            setIsLoading(true);
            
            // 1. Force the <video> element to render in the DOM first
            setShowVideo(true);

            // 2. Wait for a frame to ensure React has rendered the <video> element
            requestAnimationFrame(async () => {
                try {
                    const constraints = { 
                        video: { 
                            facingMode: 'user',
                            width: { ideal: 1280 },
                            height: { ideal: 720 }
                        } 
                    };
                    
                    // 3. Request Media
                    const stream = await navigator.mediaDevices.getUserMedia(constraints);
                    streamRef.current = stream;

                    if (videoRef.current) {
                        videoRef.current.srcObject = stream;
                        
                        // iOS specific: must be set before play()
                        videoRef.current.setAttribute('playsinline', 'true');
                        videoRef.current.muted = true;
                        
                        // 4. Explicitly play
                        await videoRef.current.play();
                        setIsWebcamActive(true);
                    }
                } catch (error) {
                    console.error("Camera Error:", error);
                    setPunchMessage('Camera access denied. Please enable camera permissions.');
                    setShowVideo(false);
                } finally {
                    setIsLoading(false);
                }
            });
            return;
        }

        // STEP 2: CAPTURE PHOTO
        if (isWebcamActive && videoRef.current) {
            const videoElement = videoRef.current;
            const canvasElement = canvasRef.current;
            const context = canvasElement.getContext('2d');
            
            canvasElement.width = videoElement.videoWidth;
            canvasElement.height = videoElement.videoHeight;
            
            context.translate(canvasElement.width, 0);
            context.scale(-1, 1);
            context.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height);
            
            const imageData = canvasElement.toDataURL('image/jpeg', 0.8);
            processPunchLog(imageData);
        }
    };

    return (
        <div className="min-h-screen p-4 sm:p-6 bg-[#f8fafc] font-sans max-w-3xl mx-auto">
            <canvas ref={canvasRef} className="hidden" />

            {/* Top Navigation */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div className="flex items-center gap-3">
                    {onBack && (
                        <button onClick={onBack} className="p-2 bg-white hover:bg-indigo-50 rounded-lg transition border border-gray-200 shadow-sm group">
                            <ArrowLeft size={18} className="text-gray-500 group-hover:text-indigo-600" />
                        </button>
                    )}
                    <div>
                        <h1 className="text-lg font-black text-gray-900 tracking-tight">Attendance Selfie</h1>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Biometric Verification</p>
                    </div>
                </div>

                <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
                    <Calendar size={14} className="text-indigo-500" />
                    <span className="text-[11px] font-bold text-gray-400 mr-1 uppercase">History:</span>
                    <input 
                        type="date" 
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="text-[12px] font-black text-gray-700 outline-none bg-transparent cursor-pointer"
                    />
                </div>
            </div>

            {/* Main Action Card */}
            <div className="bg-white p-5 rounded-3xl shadow-sm border border-gray-200 mb-6">
                <div className="flex justify-between items-center mb-4 px-1">
                    <div className="text-2xl font-black text-gray-800 tabular-nums">
                        {formatTime(currentTime)}
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] font-black text-indigo-600 uppercase tracking-tighter">Current Session</div>
                        <div className="text-[11px] font-bold text-gray-400">{formatTime(currentTime, 'date')}</div>
                    </div>
                </div>

                <div className="relative aspect-[16/9] bg-gray-950 rounded-2xl overflow-hidden mb-5 border border-gray-100">
                    {capturedImage ? (
                        <img src={capturedImage} alt="Preview" className="w-full h-full object-cover" />
                    ) : showVideo ? (
                        <>
                            <video 
                                ref={videoRef} 
                                autoPlay 
                                playsInline 
                                muted 
                                className="w-full h-full object-cover scale-x-[-1]" 
                            />
                            {/* Oval Overlay */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-[180px] h-[240px] rounded-[50%] border-2 border-dashed border-white/50 shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]" />
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[190px] h-[250px] rounded-[50%] border border-white/20" />
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full space-y-3 px-6 text-center">
                            {isLoading ? (
                                <div className="animate-pulse flex space-x-2">
                                    <div className="h-2 w-2 bg-indigo-500 rounded-full"></div>
                                    <div className="h-2 w-2 bg-indigo-500 rounded-full"></div>
                                    <div className="h-2 w-2 bg-indigo-500 rounded-full"></div>
                                </div>
                            ) : (
                                <div className="p-4 bg-gray-900/50 rounded-full mb-2">
                                    {hasCamera ? <Camera size={32} className="text-gray-600" /> : <VideoOff size={32} className="text-rose-400" />}
                                </div>
                            )}
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest leading-relaxed">
                                {isLoading ? 'Initializing Camera...' : 
                                 !hasCamera ? 'Camera not available on this device' : 
                                 'Camera inactive. Click button below to start verification.'}
                            </span>
                        </div>
                    )}
                    
                    {/* Live Badge */}
                    {isWebcamActive && !capturedImage && (
                        <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/50 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10 z-10">
                            <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                            <span className="text-[9px] text-white font-black uppercase tracking-widest">Secure Live</span>
                        </div>
                    )}
                </div>

                <div className="flex gap-3">
                    <motion.button
                        whileTap={{ scale: 0.98 }}
                        onClick={handlePunch}
                        disabled={isLoading}
                        className={`flex-1 py-3.5 rounded-2xl font-black text-[11px] uppercase tracking-[0.15em] shadow-lg transition-all flex items-center justify-center gap-2
                            ${isLoading ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 
                                (isWebcamActive || (!hasCamera && !capturedImage)) 
                                ? (isPunchedIn ? 'bg-rose-600 shadow-rose-100 text-white' : 'bg-indigo-600 shadow-indigo-100 text-white') 
                                : 'bg-gray-900 text-white'}`}
                    >
                        {!isWebcamActive && !capturedImage && hasCamera ? <Camera size={14}/> : (isPunchedIn ? <LogOut size={14}/> : <LogIn size={14}/>)}
                        {!isWebcamActive && !capturedImage && hasCamera ? 'Open Camera' : (isPunchedIn ? 'Confirm Check Out' : 'Confirm Check In')}
                    </motion.button>
                </div>

                <AnimatePresence>
                    {punchMessage && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-4 overflow-hidden">
                            <div className={`text-[11px] text-center font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 ${punchMessage.includes('denied') ? 'text-rose-600 bg-rose-50 border border-rose-100' : 'text-emerald-600 bg-emerald-50 border border-emerald-100'}`}>
                                {punchMessage.includes('denied') ? <AlertTriangle size={14} /> : <CheckCircle size={14} />}
                                {punchMessage}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Attendance History */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <div className="flex items-center gap-2">
                        <Search size={14} className="text-gray-400" />
                        <h3 className="text-[11px] font-black text-gray-500 uppercase tracking-widest">Log History</h3>
                    </div>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-gray-100">
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Type</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Time</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {filteredHistory.length > 0 ? (
                                filteredHistory.map((log) => (
                                    <tr key={log.id} className="hover:bg-indigo-50/30 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-1.5 h-1.5 rounded-full ${log.type === 'Check In' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                                <span className={`text-[11px] font-black uppercase tracking-tight ${log.type === 'Check In' ? 'text-emerald-700' : 'text-rose-700'}`}>
                                                    {log.type}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-1.5 text-gray-600">
                                                <Clock size={12} className="text-gray-300" />
                                                <span className="text-[12px] font-bold tabular-nums">{log.time}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="inline-flex items-center gap-1.5 text-[10px] font-black text-indigo-600 bg-white border border-indigo-100 px-2.5 py-1 rounded-lg shadow-sm">
                                                <CheckCircle size={10} className="text-indigo-400" />
                                                {log.status.toUpperCase()}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3" className="px-6 py-12 text-center">
                                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">No logs recorded for this date</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AttendanceSelfie;